import {
  useCallback, useState, useEffect, useMemo
} from 'react'
import {useDispatch} from 'redux-react-hook'
import isNumber from 'lodash.isnumber'
import storge from 'utils/localStorage'
import useOrderbookMedian from 'hooks/useOrderbookMedian'
import useOrderbook from 'hooks/useOrderbook'
import useSymbolInfo from 'hooks/useSymbolInfo'
import useTicker from 'hooks/useTicker'
import useRouter from 'hooks/useRouter'
import {Card, OrderBook} from '@binance/uikit'
import {updateOrderPrice} from 'actions'
import PERFORMANCE from 'performance'
import useAggTrades from 'hooks/useAggTrades'

const {DEPTH_LIMIT} = PERFORMANCE
export const treeLevels = [0, 1, 2, 3, 4, 5]
export const LEVELS = treeLevels.length
export const treeLevelsMapTickSize = {
  0: 100,
  1: 50,
  2: 10,
  3: 1,
  4: 0.1,
  5: 0.01
}
export const treeLevelsMapDecimal = {
  0: 0,
  1: 0,
  2: 0,
  3: 0,
  4: 1,
  5: 2
}

export default function OrderBookView ({i18n}) {
  const dispatch = useDispatch()
  const router = useRouter()
  const {symbol} = router.query
  const {symbolInfo} = useSymbolInfo()
  const {quoteAsset, pricePrecision, quantityPrecision} = symbolInfo

  // tickSize
  const defaultLevel = LEVELS - 1
  const [level, setLevel] = useState(defaultLevel)

  const tickSize = treeLevelsMapTickSize[level]
  const asks = useOrderbook({
    symbol,
    type: 'ask',
    limit: DEPTH_LIMIT,
    tickSize,
    mergeDesc: false,
    orderDesc: true
  })

  const bids = useOrderbook({
    symbol,
    type: 'bid',
    limit: DEPTH_LIMIT,
    tickSize,
    mergeDesc: true,
    orderDesc: true
  })

  const median = useOrderbookMedian(symbol, tickSize)

  const {markPrice, lastPriceSign} = useTicker(symbol)
  const aggTrades = useAggTrades(symbol)
  const contractPrice = aggTrades?.[0]?.price
  const handleLevelChange = useCallback(v => {
    setLevel(v)
    storge.setItem('level', v)
  }, [])

  useEffect(() => {
    const _v = storge.getItem('level')
    const v = isNumber(_v) ? _v : defaultLevel
    setLevel(v)
  }, [defaultLevel])

  return useMemo(
    () => (
      <Card gridArea="orderbook">
        <OrderBook
          treeLevels={treeLevels}
          treeLevelsMapTickSize={treeLevelsMapTickSize}
          treeLevelsMapDecimal={treeLevelsMapDecimal}
          asks={asks}
          bids={bids}
          median={median}
          contractPrice={contractPrice}
          markPrice={markPrice}
          tickSize={tickSize}
          level={level}
          decimalPlace={pricePrecision}
          defaultLevel={level}
          handleDecimalChange={handleLevelChange}
          lastPriceSign={lastPriceSign}
          handlePriceClick={(buyOrSell, price) => {
            dispatch(updateOrderPrice('buy', price, 'limit'))
            dispatch(updateOrderPrice('sell', price, 'limit'))
            dispatch(updateOrderPrice('buy', price, 'stop'))
            dispatch(updateOrderPrice('sell', price, 'stop'))
          }}
          isConnected
          quoteAsset={quoteAsset}
          quantityPrecision={quantityPrecision}
          i18n={i18n}
        />
      </Card>
    ),
    [
      asks,
      bids,
      median,
      contractPrice,
      markPrice,
      tickSize,
      level,
      pricePrecision,
      handleLevelChange,
      lastPriceSign,
      quoteAsset,
      quantityPrecision,
      i18n,
      dispatch
    ]
  )
}
